import { useEffect, useRef, useState } from 'react'
import { Sheet } from '../ui/Sheet'
import { Icon } from '../ui/Icon'
import { BffError } from '../transport/relay'
import { FIRST_TAG, pinKey, tagOf, usePins, type PinTarget } from './usePins'

/** As long as the BFF keeps (agent_pins.TAG_NAME_MAX) — it cuts, this stops the typing. */
const NAME_MAX = 40

/**
 * Why a tag could not be made, renamed or removed, in the reader's words. The BFF
 * says which refusal it was (`code`); what the person is told is written here, where
 * the name they typed is still on screen.
 */
function whyNot(e: unknown, name: string): string {
  const code = e instanceof BffError ? e.code : null
  if (code === 'tag_exists') return `There is already a tag named “${name}”.`
  if (code === 'too_many_tags') return 'That is as many tags as an account holds. Delete one first.'
  if (code === 'unknown_tag') return 'That tag was deleted on another device.'
  if (e instanceof BffError && e.status === 404) return 'This server does not have tags yet.'
  return `Couldn’t save — ${e instanceof Error ? e.message : String(e)}`
}

/**
 * THE PIN SHEET — owner, 2026-10-12: 當使用者按下 Pin 的這個按鈕的時候, 就會彈出一個視窗,
 * Default 就是 Pinned 的那一個 Tag, 但是使用者可以新增另外一種不同的 Tag, 之後就可以輕易地
 * 選擇不同的 Tag 加到某些 Agent 裡面去, 或者是定義新的 pin Tag.
 *
 * ONE LIST OF ROWS (his taste, 2026-09-30, and the agent card's idiom): the tags, then
 * New tag, then Edit tags. Pressing a tag pins the agent under it and the sheet is
 * gone — ONE TAG PER AGENT (he was asked), so picking another tag is the move, and
 * pressing the one it is already under unpins. Nothing here is a setting to confirm.
 *
 * A row that needs more opens its own small view in place: New tag asks for a name
 * (and pins this agent under it — a tag is made by pinning under it, so there is
 * never one with nothing in it by accident), Edit tags leads to a tag's name and
 * its Delete.
 *
 * Opened two ways: from an agent's pin (`target`), and from a tag heading's pencil
 * (`editTag`, no agent) — straight onto that tag's view, and closed when it is done.
 */
export function PinTagSheet({
  target,
  editTag,
  onClose,
}: {
  /** The agent whose pin was pressed. */
  target?: PinTarget | undefined
  /** Open on this tag's own view — the heading's pencil. */
  editTag?: string | undefined
  onClose: () => void
}) {
  const { pins, tags, place, placeNew, unpin, renameTag, removeTag } = usePins()
  const [view, setView] = useState<'list' | 'new' | 'tags' | { edit: string }>(
    editTag ? { edit: editTag } : 'list',
  )
  const [name, setName] = useState(() => tags.find((t) => t.id === editTag)?.name ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [armed, setArmed] = useState(false)
  const field = useRef<HTMLInputElement>(null)

  /* Escape is the keyboard's ✕. On the document, not the dialog: the press that opened
     this left the focus on the rail's pin button, outside the dialog. And in the
     CAPTURE phase: that button stops its key presses from reaching the row behind it,
     which stops them reaching a listener that waits for them to bubble here, too. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])

  /* New tag opens for typing — the person chose it, and the name is all it asks. (The
     rename field does NOT take the focus: its view is also where Delete is, and on a
     phone a keyboard nobody asked for would be standing on it.) */
  useEffect(() => {
    if (view === 'new') field.current?.focus()
  }, [view])

  const mine = target ? pins.find((p) => pinKey(p.device, p.hash) === pinKey(target.device, target.hash)) : undefined
  const current = mine ? tagOf(mine) : null
  const editing = typeof view === 'object' ? tags.find((t) => t.id === view.edit) : undefined
  /* The tag on screen was removed elsewhere (another device): there is nothing left
     to edit. From an agent's pin, fall back to the list; from a heading, close. */
  const orphaned = typeof view === 'object' && !editing
  useEffect(() => {
    if (!orphaned) return
    if (target) setView('tags')
    else onClose()
  }, [orphaned, target, onClose])

  const go = (next: typeof view, startName = '') => {
    setView(next)
    setName(startName)
    setErr(null)
    setArmed(false)
  }
  /** Where an edit or a new tag returns to when it is done. */
  const done = () => (target ? go(typeof view === 'object' ? 'tags' : 'list') : onClose())

  const typed = name.trim()
  const run = async (what: () => Promise<unknown>, then: () => void) => {
    setBusy(true)
    setErr(null)
    try {
      await what()
      then()
    } catch (e) {
      setErr(whyNot(e, typed))
    } finally {
      setBusy(false)
    }
  }
  const create = () => {
    if (target && typed && !busy) void run(() => placeNew(target, typed), onClose)
  }
  const save = () => {
    if (!editing || !typed || busy) return
    if (typed === editing.name) return done()
    void run(() => renameTag(editing.id, typed), done)
  }

  const form = (onSubmit: () => void) => (
    <form
      className="pt-form"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <input
        ref={field}
        value={name}
        onChange={(e) => {
          setName(e.target.value)
          setErr(null)
        }}
        maxLength={NAME_MAX}
        aria-label="Tag name"
        placeholder="Tag name"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
      />
      {err && (
        <p className="err-line" role="alert">
          {err}
        </p>
      )}
    </form>
  )
  const actions = (label: string, working: string, ready: boolean, onGo: () => void) => (
    <>
      <button type="button" className="sheet-btn" onClick={() => (view === 'new' ? go('list') : done())}>
        Cancel
      </button>
      <button type="button" className="sheet-btn primary" disabled={!ready || busy} onClick={onGo}>
        {busy ? working : label}
      </button>
    </>
  )

  return (
    <Sheet
      title={target ? `Pin ${target.session}` : 'Edit tag'}
      onClose={onClose}
      className="pt-sheet"
      actions={
        view === 'new'
          ? actions('Create', 'Creating…', typed !== '', create)
          : editing
            ? actions('Save', 'Saving…', typed !== '', save)
            : undefined
      }
    >
      {view === 'list' && target && (
        <div className="ac-menu">
          {tags.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`ac-row pt-tag${current === t.id ? ' cur' : ''}`}
              aria-pressed={current === t.id}
              onClick={() => {
                if (current === t.id) unpin(target)
                else place(target, t.id)
                onClose()
              }}
            >
              <Icon name="tag" size={18} />
              <span className="ac-row-label">{t.name}</span>
              {/* Decorative: `aria-pressed` is what says it, and the name stays the tag's. */}
              {current === t.id && <Icon name="check" size={18} className="pt-check" />}
            </button>
          ))}
          <button type="button" className="ac-row" onClick={() => go('new')}>
            <Icon name="add" size={18} />
            <span className="ac-row-label">New tag</span>
          </button>
          <button type="button" className="ac-row" onClick={() => go('tags')}>
            <Icon name="edit" size={18} />
            <span className="ac-row-label">Edit tags</span>
            <Icon name="chevron_right" size={18} className="ac-row-go" />
          </button>
        </div>
      )}

      {view === 'new' && (
        <>
          <button type="button" className="ac-back" aria-label="Back" onClick={() => go('list')}>
            <Icon name="chevron_left" size={18} />
            New tag
          </button>
          {form(create)}
        </>
      )}

      {(view === 'tags' || (orphaned && target)) && (
        <>
          <button type="button" className="ac-back" aria-label="Back" onClick={() => go('list')}>
            <Icon name="chevron_left" size={18} />
            Edit tags
          </button>
          <div className="ac-menu">
            {tags.map((t) => (
              <button key={t.id} type="button" className="ac-row" onClick={() => go({ edit: t.id }, t.name)}>
                <Icon name="tag" size={18} />
                <span className="ac-row-label">{t.name}</span>
                <Icon name="chevron_right" size={18} className="ac-row-go" />
              </button>
            ))}
          </div>
        </>
      )}

      {editing && (
        <>
          {target && (
            <button type="button" className="ac-back" aria-label="Back" onClick={() => go('tags')}>
              <Icon name="chevron_left" size={18} />
              {editing.name}
            </button>
          )}
          {form(save)}
          {/* The first tag is the one every account has: renamed, never removed
              (the agents of a removed tag go to it). */}
          {editing.id !== FIRST_TAG && (
            <div className="ac-menu">
              <button
                type="button"
                className="ac-row ac-row-danger"
                disabled={busy}
                onClick={() => {
                  if (!armed) return setArmed(true)
                  void run(() => removeTag(editing.id), done)
                }}
              >
                <Icon name="delete" size={18} />
<span className="ac-row-label">{armed ? 'Tap again to delete' : 'Delete tag'}</span>
              </button>
            </div>
          )}
        </>
      )}
    </Sheet>
  )
}
